import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { normalizeRfidTag } from "./finishTabletPresentation";
import { getRfidTransport, type RfidPort } from "./cp210xSerial";

export type RfidSerialState = "unsupported" | "disconnected" | "connecting" | "connected" | "error";

/** Reads newline-delimited (or briefly buffered) RFID values from the shared 9600 baud reader. */
export function useRfidSerial(onTag: (tag: string) => void, enabled = true) {
  const transport = useMemo(getRfidTransport, []);
  const supported = Boolean(transport);
  const [state, setState] = useState<RfidSerialState>(() => supported ? "disconnected" : "unsupported");
  const [message, setMessage] = useState("");
  const portRef = useRef<RfidPort | null>(null);
  const readerRef = useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null);
  const readingRef = useRef(false);
  const callbackRef = useRef(onTag);
  const bufferRef = useRef("");
  const flushTimerRef = useRef<number | null>(null);
  const lastScanRef = useRef({ tag: "", at: 0 });
  const lifecycleRef = useRef(0);
  const requestingRef = useRef(false);
  callbackRef.current = onTag;

  const consumeTag = useCallback((raw: string) => {
    const tag = normalizeRfidTag(raw);
    if (!tag || !/^[A-Z0-9:_-]{1,128}$/.test(tag)) return;
    const now = Date.now();
    if (lastScanRef.current.tag === tag && now - lastScanRef.current.at < 1_200) return;
    lastScanRef.current = { tag, at: now };
    callbackRef.current(tag);
  }, []);

  const consumeChunk = useCallback((chunk: string) => {
    if (flushTimerRef.current !== null) window.clearTimeout(flushTimerRef.current);
    bufferRef.current += chunk;
    const lines = bufferRef.current.split(/\r?\n|\r/);
    bufferRef.current = lines.pop() ?? "";
    lines.forEach(consumeTag);
    if (bufferRef.current) {
      flushTimerRef.current = window.setTimeout(() => {
        const buffered = bufferRef.current;
        bufferRef.current = "";
        consumeTag(buffered);
      }, 100);
    }
  }, [consumeTag]);

  const disconnect = useCallback(async () => {
    const port = portRef.current;
    portRef.current = null;
    readingRef.current = false;
    if (flushTimerRef.current !== null) window.clearTimeout(flushTimerRef.current);
    flushTimerRef.current = null;
    bufferRef.current = "";
    await readerRef.current?.cancel().catch(() => undefined);
    readerRef.current = null;
    await port?.close().catch(() => undefined);
    setState(supported ? "disconnected" : "unsupported");
    setMessage("");
  }, [supported]);

  const open = useCallback(async (port: RfidPort) => {
    if (portRef.current || readingRef.current || !enabled) return;
    portRef.current = port;
    setState("connecting");
    setMessage("");
    try {
      await port.open({ baudRate: 9_600 });
      if (portRef.current !== port) { await port.close(); return; }
      readingRef.current = true;
      setState("connected");
      const decoder = new TextDecoder();
      while (port.readable && readingRef.current) {
        const reader = port.readable.getReader();
        readerRef.current = reader;
        try {
          while (readingRef.current) {
            const { value, done } = await reader.read();
            if (done) { readingRef.current = false; break; }
            if (value) consumeChunk(decoder.decode(value, { stream: true }));
          }
        } finally {
          reader.releaseLock();
          if (readerRef.current === reader) readerRef.current = null;
        }
      }
    } catch (error) {
      if (portRef.current === port) {
        setState("error");
        setMessage(error instanceof Error ? error.message : "RFID 리더기를 연결하지 못했습니다.");
      }
    } finally {
      if (portRef.current === port) {
        readingRef.current = false;
        portRef.current = null;
        if (flushTimerRef.current !== null) window.clearTimeout(flushTimerRef.current);
        bufferRef.current = "";
        setState(current => current === "connected" ? "disconnected" : current);
      }
      await port.close().catch(() => undefined);
    }
  }, [consumeChunk, enabled]);

  const connect = useCallback(async () => {
    if (!enabled || !transport || requestingRef.current || portRef.current) return;
    const lifecycle = lifecycleRef.current;
    requestingRef.current = true;
    try {
      const port = await transport.requestPort();
      if (lifecycle === lifecycleRef.current) await open(port);
    }
    catch (error) {
      if (error instanceof DOMException && error.name === "NotFoundError") return;
      setState("error");
      setMessage(error instanceof Error ? error.message : "RFID 리더기를 선택하지 못했습니다.");
    } finally { requestingRef.current = false; }
  }, [enabled, open, transport]);

  useEffect(() => {
    if (!enabled || !transport) return;
    let active = true;
    void transport.getPorts().then((ports) => {
      if (active && ports[0]) void open(ports[0]);
    }).catch(() => { /* Permission can expire; the connection button allows a new selection. */ });
    const onDisconnect = () => { void disconnect(); };
    const unsubscribe = transport.subscribeDisconnect(onDisconnect);
    return () => {
      lifecycleRef.current++;
      active = false;
      unsubscribe();
      void disconnect();
    };
  }, [disconnect, enabled, open, transport]);

  return { state, message, connect, disconnect };
}
